// src/store/surveyBuilderStore.ts
import { create } from 'zustand';
import { v4 as uuidv4 } from 'uuid';
import {
  Survey,
  SurveyQuestion,
  QuestionType,
  ConditionalLogicRule,
  SkipPattern,
  SurveyValidation,
} from '../types/survey.types';

interface SurveyBuilderState {
  // Current survey being edited
  survey: Survey | null;

  // UI state
  selectedQuestionId: string | null;
  isDragging: boolean;
  isPreviewMode: boolean;
  isSaving: boolean;
  hasUnsavedChanges: boolean;

  // Clipboard for copy/paste
  clipboard: SurveyQuestion | null;

  // Undo/redo history
  history: Survey[];
  historyIndex: number;

  // Validation errors
  validationErrors: Record<string, string[]>;
}

interface SurveyBuilderActions {
  // Survey actions
  setSurvey: (survey: Survey | null) => void;
  createNewSurvey: (projectId: string, name: string) => void;
  updateSurveyName: (name: string) => void;
  updateSurveyDescription: (description: string) => void;
  updateSurveyProject: (projectId: string) => void;
  updateSurveySettings: (settings: Partial<Survey['settings']>) => void;

  // Question actions
  addQuestion: (type: QuestionType, afterId?: string) => void;
  updateQuestion: (questionId: string, updates: Partial<SurveyQuestion>) => void;
  deleteQuestion: (questionId: string) => void;
  duplicateQuestion: (questionId: string) => void;
  reorderQuestions: (fromIndex: number, toIndex: number) => void;
  moveQuestion: (questionId: string, direction: 'up' | 'down') => void;

  // Selection
  selectQuestion: (questionId: string | null) => void;

  // Conditional logic
  addConditionalRule: (rule: Omit<ConditionalLogicRule, 'id'>) => void;
  updateConditionalRule: (ruleId: string, updates: Partial<ConditionalLogicRule>) => void;
  deleteConditionalRule: (ruleId: string) => void;

  // Skip patterns
  addSkipPattern: (pattern: Omit<SkipPattern, 'id'>) => void;
  updateSkipPattern: (patternId: string, updates: Partial<SkipPattern>) => void;
  deleteSkipPattern: (patternId: string) => void;

  // Clipboard
  copyQuestion: (questionId: string) => void;
  pasteQuestion: (afterId?: string) => void;

  // Undo/redo
  undo: () => void;
  redo: () => void;
  saveToHistory: () => void;

  // Preview
  setPreviewMode: (isPreview: boolean) => void;

  // Validation
  validateSurvey: () => boolean;
  clearValidationErrors: () => void;

  // Save state
  setSaving: (isSaving: boolean) => void;
  markAsSaved: () => void;

  // Reset
  reset: () => void;
}

type SurveyBuilderStore = SurveyBuilderState & SurveyBuilderActions;

const initialState: SurveyBuilderState = {
  survey: null,
  selectedQuestionId: null,
  isDragging: false,
  isPreviewMode: false,
  isSaving: false,
  hasUnsavedChanges: false,
  clipboard: null,
  history: [],
  historyIndex: -1,
  validationErrors: {},
};

const createDefaultQuestion = (type: QuestionType, order: number): SurveyQuestion => {
  const baseQuestion: SurveyQuestion = {
    id: uuidv4(),
    order,
    type,
    text: '',
    description: '',
    required: false,
    validation: {},
    defaultVisible: true,
  };

  switch (type) {
    case 'yes_no':
      return {
        ...baseQuestion,
        text: 'New Yes/No Question',
      };
    case 'multiple_choice':
      return {
        ...baseQuestion,
        text: 'New Multiple Choice Question',
        options: [
          { id: uuidv4(), text: 'Option 1', order: 0 },
          { id: uuidv4(), text: 'Option 2', order: 1 },
        ],
      };
    case 'multi_select':
      return {
        ...baseQuestion,
        text: 'New Multi-Select Question',
        options: [
          { id: uuidv4(), text: 'Option 1', order: 0 },
          { id: uuidv4(), text: 'Option 2', order: 1 },
        ],
      };
    case 'dropdown':
      return {
        ...baseQuestion,
        text: 'New Dropdown Question',
        options: [
          { id: uuidv4(), text: 'Option 1', order: 0 },
          { id: uuidv4(), text: 'Option 2', order: 1 },
        ],
      };
    case 'text':
      return {
        ...baseQuestion,
        text: 'New Text Question',
        validation: {
          maxLength: 500,
        },
      };
    case 'number':
      return {
        ...baseQuestion,
        text: 'New Number Question',
      };
    case 'rating_scale':
      return {
        ...baseQuestion,
        text: 'New Rating Question',
        ratingConfig: {
          min: 1,
          max: 5,
          minLabel: 'Poor',
          maxLabel: 'Excellent',
        },
      };
    case 'date':
      return {
        ...baseQuestion,
        text: 'New Date Question',
      };
    case 'phone':
      return {
        ...baseQuestion,
        text: 'Phone Number',
      };
    case 'email':
      return {
        ...baseQuestion,
        text: 'Email Address',
      };
    default:
      return baseQuestion;
  }
};

export const useSurveyBuilderStore = create<SurveyBuilderStore>((set, get) => ({
  ...initialState,

  // Survey actions
  setSurvey: (survey) => {
    set({
      survey,
      selectedQuestionId: null,
      hasUnsavedChanges: false,
      history: survey ? [survey] : [],
      historyIndex: 0,
      validationErrors: {},
    });
  },

  createNewSurvey: (projectId, name) => {
    const newSurvey: Survey = {
      id: uuidv4(),
      projectId,
      name,
      description: '',
      version: 1,
      status: 'draft',
      questions: [],
      conditionalLogic: [],
      skipPatterns: [],
      settings: {
        allowBack: true,
        showProgress: true,
        randomizeQuestions: false,
        requireAllQuestions: false,
        submitButtonText: 'Submit',
        completionMessage: 'Thank you for completing the survey!',
      },
      createdBy: '',
      createdAt: new Date() as any,
      updatedAt: new Date() as any,
    };

    set({
      survey: newSurvey,
      hasUnsavedChanges: true,
      history: [newSurvey],
      historyIndex: 0,
    });
  },

  updateSurveyName: (name) => {
    const { survey } = get();
    if (!survey) return;

    set({
      survey: { ...survey, name },
      hasUnsavedChanges: true,
    });
  },

  updateSurveyDescription: (description) => {
    const { survey } = get();
    if (!survey) return;

    set({
      survey: { ...survey, description },
      hasUnsavedChanges: true,
    });
  },

  updateSurveyProject: (projectId) => {
    const { survey } = get();
    if (!survey) return;

    set({
      survey: { ...survey, projectId },
      hasUnsavedChanges: true,
    });
  },

  updateSurveySettings: (settings) => {
    const { survey } = get();
    if (!survey) return;

    set({
      survey: {
        ...survey,
        settings: { ...survey.settings, ...settings },
      },
      hasUnsavedChanges: true,
    });
  },

  // Question actions
  addQuestion: (type, afterId) => {
    const { survey, saveToHistory } = get();
    if (!survey) return;

    saveToHistory();

    let insertIndex = survey.questions.length;
    if (afterId) {
      const afterIndex = survey.questions.findIndex((q) => q.id === afterId);
      if (afterIndex !== -1) {
        insertIndex = afterIndex + 1;
      }
    }

    const newQuestion = createDefaultQuestion(type, insertIndex);
    const updatedQuestions = [...survey.questions];
    updatedQuestions.splice(insertIndex, 0, newQuestion);

    // Reorder all questions
    const reorderedQuestions = updatedQuestions.map((q, index) => ({
      ...q,
      order: index,
    }));

    set({
      survey: { ...survey, questions: reorderedQuestions },
      selectedQuestionId: newQuestion.id,
      hasUnsavedChanges: true,
    });
  },

  updateQuestion: (questionId, updates) => {
    const { survey, saveToHistory } = get();
    if (!survey) return;

    saveToHistory();

    const updatedQuestions = survey.questions.map((q) =>
      q.id === questionId ? { ...q, ...updates } : q
    );

    set({
      survey: { ...survey, questions: updatedQuestions },
      hasUnsavedChanges: true,
    });
  },

  deleteQuestion: (questionId) => {
    const { survey, saveToHistory, selectedQuestionId } = get();
    if (!survey) return;

    saveToHistory();

    const updatedQuestions = survey.questions
      .filter((q) => q.id !== questionId)
      .map((q, index) => ({ ...q, order: index }));

    // Also remove any conditional logic or skip patterns referencing this question
    const updatedConditionalLogic = survey.conditionalLogic.filter(
      (rule) => rule.sourceQuestionId !== questionId && rule.targetQuestionId !== questionId
    );
    const updatedSkipPatterns = survey.skipPatterns.filter(
      (pattern) => pattern.questionId !== questionId && pattern.skipToQuestionId !== questionId
    );

    set({
      survey: {
        ...survey,
        questions: updatedQuestions,
        conditionalLogic: updatedConditionalLogic,
        skipPatterns: updatedSkipPatterns,
      },
      selectedQuestionId: selectedQuestionId === questionId ? null : selectedQuestionId,
      hasUnsavedChanges: true,
    });
  },

  duplicateQuestion: (questionId) => {
    const { survey, saveToHistory } = get();
    if (!survey) return;

    const question = survey.questions.find((q) => q.id === questionId);
    if (!question) return;

    saveToHistory();

    const duplicatedQuestion: SurveyQuestion = {
      ...question,
      id: uuidv4(),
      text: `${question.text} (Copy)`,
      order: question.order + 1,
      options: question.options?.map((opt) => ({
        ...opt,
        id: uuidv4(),
      })),
    };

    const insertIndex = question.order + 1;
    const updatedQuestions = [...survey.questions];
    updatedQuestions.splice(insertIndex, 0, duplicatedQuestion);

    const reorderedQuestions = updatedQuestions.map((q, index) => ({
      ...q,
      order: index,
    }));

    set({
      survey: { ...survey, questions: reorderedQuestions },
      selectedQuestionId: duplicatedQuestion.id,
      hasUnsavedChanges: true,
    });
  },

  reorderQuestions: (fromIndex, toIndex) => {
    const { survey, saveToHistory } = get();
    if (!survey) return;

    saveToHistory();

    const questions = [...survey.questions];
    const [movedQuestion] = questions.splice(fromIndex, 1);
    questions.splice(toIndex, 0, movedQuestion);

    const reorderedQuestions = questions.map((q, index) => ({
      ...q,
      order: index,
    }));

    set({
      survey: { ...survey, questions: reorderedQuestions },
      hasUnsavedChanges: true,
    });
  },

  moveQuestion: (questionId, direction) => {
    const { survey, reorderQuestions } = get();
    if (!survey) return;

    const currentIndex = survey.questions.findIndex((q) => q.id === questionId);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= survey.questions.length) return;

    reorderQuestions(currentIndex, newIndex);
  },

  // Selection
  selectQuestion: (questionId) => {
    set({ selectedQuestionId: questionId });
  },

  // Conditional logic
  addConditionalRule: (rule) => {
    const { survey, saveToHistory } = get();
    if (!survey) return;

    saveToHistory();

    const newRule: ConditionalLogicRule = {
      ...rule,
      id: uuidv4(),
    };

    set({
      survey: {
        ...survey,
        conditionalLogic: [...survey.conditionalLogic, newRule],
      },
      hasUnsavedChanges: true,
    });
  },

  updateConditionalRule: (ruleId, updates) => {
    const { survey, saveToHistory } = get();
    if (!survey) return;

    saveToHistory();

    const updatedRules = survey.conditionalLogic.map((rule) =>
      rule.id === ruleId ? { ...rule, ...updates } : rule
    );

    set({
      survey: { ...survey, conditionalLogic: updatedRules },
      hasUnsavedChanges: true,
    });
  },

  deleteConditionalRule: (ruleId) => {
    const { survey, saveToHistory } = get();
    if (!survey) return;

    saveToHistory();

    const updatedRules = survey.conditionalLogic.filter((rule) => rule.id !== ruleId);

    set({
      survey: { ...survey, conditionalLogic: updatedRules },
      hasUnsavedChanges: true,
    });
  },

  // Skip patterns
  addSkipPattern: (pattern) => {
    const { survey, saveToHistory } = get();
    if (!survey) return;

    saveToHistory();

    const newPattern: SkipPattern = {
      ...pattern,
      id: uuidv4(),
    };

    set({
      survey: {
        ...survey,
        skipPatterns: [...survey.skipPatterns, newPattern],
      },
      hasUnsavedChanges: true,
    });
  },

  updateSkipPattern: (patternId, updates) => {
    const { survey, saveToHistory } = get();
    if (!survey) return;

    saveToHistory();

    const updatedPatterns = survey.skipPatterns.map((pattern) =>
      pattern.id === patternId ? { ...pattern, ...updates } : pattern
    );

    set({
      survey: { ...survey, skipPatterns: updatedPatterns },
      hasUnsavedChanges: true,
    });
  },

  deleteSkipPattern: (patternId) => {
    const { survey, saveToHistory } = get();
    if (!survey) return;

    saveToHistory();

    const updatedPatterns = survey.skipPatterns.filter((pattern) => pattern.id !== patternId);

    set({
      survey: { ...survey, skipPatterns: updatedPatterns },
      hasUnsavedChanges: true,
    });
  },

  // Clipboard
  copyQuestion: (questionId) => {
    const { survey } = get();
    if (!survey) return;

    const question = survey.questions.find((q) => q.id === questionId);
    if (question) {
      set({ clipboard: { ...question } });
    }
  },

  pasteQuestion: (afterId) => {
    const { clipboard, addQuestion, survey, saveToHistory } = get();
    if (!clipboard || !survey) return;

    saveToHistory();

    let insertIndex = survey.questions.length;
    if (afterId) {
      const afterIndex = survey.questions.findIndex((q) => q.id === afterId);
      if (afterIndex !== -1) {
        insertIndex = afterIndex + 1;
      }
    }

    const pastedQuestion: SurveyQuestion = {
      ...clipboard,
      id: uuidv4(),
      order: insertIndex,
      text: `${clipboard.text} (Pasted)`,
      options: clipboard.options?.map((opt) => ({
        ...opt,
        id: uuidv4(),
      })),
    };

    const updatedQuestions = [...survey.questions];
    updatedQuestions.splice(insertIndex, 0, pastedQuestion);

    const reorderedQuestions = updatedQuestions.map((q, index) => ({
      ...q,
      order: index,
    }));

    set({
      survey: { ...survey, questions: reorderedQuestions },
      selectedQuestionId: pastedQuestion.id,
      hasUnsavedChanges: true,
    });
  },

  // Undo/redo
  saveToHistory: () => {
    const { survey, history, historyIndex } = get();
    if (!survey) return;

    // Remove any future history if we're not at the end
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(JSON.parse(JSON.stringify(survey)));

    // Limit history to 50 items
    if (newHistory.length > 50) {
      newHistory.shift();
    }

    set({
      history: newHistory,
      historyIndex: newHistory.length - 1,
    });
  },

  undo: () => {
    const { history, historyIndex } = get();
    if (historyIndex <= 0) return;

    const newIndex = historyIndex - 1;
    set({
      survey: JSON.parse(JSON.stringify(history[newIndex])),
      historyIndex: newIndex,
      hasUnsavedChanges: true,
    });
  },

  redo: () => {
    const { history, historyIndex } = get();
    if (historyIndex >= history.length - 1) return;

    const newIndex = historyIndex + 1;
    set({
      survey: JSON.parse(JSON.stringify(history[newIndex])),
      historyIndex: newIndex,
      hasUnsavedChanges: true,
    });
  },

  // Preview
  setPreviewMode: (isPreview) => {
    set({ isPreviewMode: isPreview });
  },

  // Validation
  validateSurvey: () => {
    const { survey } = get();
    if (!survey) return false;

    const errors: Record<string, string[]> = {};

    // Survey-level validation
    if (!survey.name.trim()) {
      errors.survey = ['Survey name is required'];
    }

    // Question validation
    survey.questions.forEach((question) => {
      const questionErrors: string[] = [];

      if (!question.text.trim()) {
        questionErrors.push('Question text is required');
      }

      if (
        ['multiple_choice', 'multi_select', 'dropdown'].includes(question.type) &&
        (!question.options || question.options.length < 2)
      ) {
        questionErrors.push('At least 2 options are required');
      }

      if (question.options) {
        const emptyOptions = question.options.filter((opt) => !opt.text.trim());
        if (emptyOptions.length > 0) {
          questionErrors.push('All options must have text');
        }
      }

      if (question.type === 'rating_scale' && question.ratingConfig) {
        if (question.ratingConfig.min >= question.ratingConfig.max) {
          questionErrors.push('Rating max must be greater than min');
        }
      }

      if (questionErrors.length > 0) {
        errors[question.id] = questionErrors;
      }
    });

    set({ validationErrors: errors });
    return Object.keys(errors).length === 0;
  },

  clearValidationErrors: () => {
    set({ validationErrors: {} });
  },

  // Save state
  setSaving: (isSaving) => {
    set({ isSaving });
  },

  markAsSaved: () => {
    set({ hasUnsavedChanges: false, isSaving: false });
  },

  // Reset
  reset: () => {
    set(initialState);
  },
}));

// Selectors
export const useSelectedQuestion = () => {
  return useSurveyBuilderStore((state) => {
    if (!state.survey || !state.selectedQuestionId) return null;
    return state.survey.questions.find((q) => q.id === state.selectedQuestionId) || null;
  });
};

export const useCanUndo = () => {
  return useSurveyBuilderStore((state) => state.historyIndex > 0);
};

export const useCanRedo = () => {
  return useSurveyBuilderStore((state) => state.historyIndex < state.history.length - 1);
};
